import { Component, Element, h, Host, Prop, Watch } from '@stencil/core'

import {
  COLUMN_WIDTH_MAP,
  COLUMN_WIDTH_SEMANTICS,
  TABLE_ALIGNMENT,
  TABLE_ALIGNMENT_MAP,
  TABLE_DENSITY,
  TABLE_DENSITY_MAP,
  TABLE_DIVIDER_STYLE,
  TABLE_DIVIDER_STYLE_MAP,
  TABLE_OVERFLOW,
  TABLE_OVERFLOW_MAP,
  TABLE_TOOLTIP_STATE,
} from './table-constants'
import { TABLE_TOKENS } from './table-tokens'
import {
  ColumnWidth,
  TableAlignment,
  TableDensity,
  TableDividerStyle,
  TableOverflow,
  TableTooltipMode,
  TableTooltipState,
} from './table-types'
import { positiveFrValidator, positivePxValidator, resolveTokenValue } from './table-utils'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Tabela baseada em CSS Grid com controle via tokens.
 *
 * A tabela é composta de vários subcomponentes, incluindo:
 * - br-table-header: Define o cabeçalho da tabela, agrupando as linhas de cabeçalho.
 * - br-table-row: Representa uma linha da tabela, podendo conter células de conteúdo ou células de cabeçalho.
 * - br-table-header-cell: Representa uma célula de cabeçalho, permitindo a definição de propriedades como largura e alinhamento que podem ser herdadas pelas células de conteúdo associadas.
 * - br-table-cell: Representa uma célula de conteúdo da tabela, onde os dados são inseridos.
 * - br-table-body: Agrupa as linhas de conteúdo da tabela, representadas pelo subcomponente br-table-row.
 *
 * @remarks
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/table?tab=designer).
 *
 * @Slot header: Área para inclusão do subcomponente br-table-header
 * @Slot body: Área para inclusão do subcomponente br-table-body
 */
@Component({
  tag: 'br-table',
  styleUrl: 'table.scss',
  shadow: true,
})
export class Table {
  private readonly generatedHeaderPrefix = `br-table-header-${Math.random().toString(36).slice(2, 8)}`

  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   *
   */
  @Element() el!: HTMLBrTableElement

  /**
   * Largura global aplicada a todas as colunas.
   *
   *  @remarks Valores aceitos:
   * - fill
   * - content
   * - ${number}px
   * - ${number}fr
   *
   * Observação:
   * - `columnWidth` não define o comportamento de `overflow`, mas pode interferir na sua percepção ao alterar o espaço disponível da coluna.
   * - `content` (`max-content`) tende a expandir a coluna e reduzir casos visíveis de truncagem.
   * - `fill` (`1fr`) tende a evidenciar truncagem quando o espaço horizontal é insuficiente.
   *
   * @default fill
   */
  @Prop({ reflect: true }) columnWidth?: ColumnWidth

  /**
   * Define a densidade da tabela.
   *
   * @remarks Valores aceitos:
   * - small
   * - medium
   * - large
   *
   * @default medium
   */
  @Prop({ reflect: true }) density?: TableDensity

  /**
   * Define o estilo visual dos divisores.
   *
   * @remarks Valores aceitos:
   * - solid
   * - dashed
   *
   * @default solid
   */
  @Prop({ reflect: true }) dividerStyle?: TableDividerStyle

  /**
   * Ativa divisores verticais entre colunas.
   *
   * @default false
   */
  @Prop({ reflect: true }) hasColumnDivider?: boolean = false

  /**
   * Ativa divisores horizontais entre linhas.
   *
   * @default false
   */
  @Prop({ reflect: true }) hasRowDivider?: boolean = false

  /**
   * Define o alinhamento horizontal do conteúdo das células.
   *
   * @remarks Valores aceitos:
   * - start
   * - center
   * - end
   *
   * @default start
   */
  @Prop({ reflect: true }) horizontalAlignment?: TableAlignment

  /**
   * Define o alinhamento vertical do conteúdo das células.
   *
   * @remarks Valores aceitos:
   * - start
   * - center
   * - end
   *
   * @default start
   */
  @Prop({ reflect: true }) verticalAlignment?: TableAlignment

  /**
   * Define o comportamento de overflow para as células.
   *
   * @remarks Valores aceitos:
   * - truncate
   * - wrap
   *
   * Observação:
   * - O overflow é independente de `columnWidth`.
   * - Com `columnWidth="content"`, a coluna tende a crescer com o conteúdo (`max-content`), o que pode reduzir casos visíveis de truncagem.
   * - Com `columnWidth="fill"`, a coluna ocupa o espaço disponível (`1fr`), então a truncagem tende a ficar mais evidente quando não há espaço suficiente.
   *
   * @default wrap
   */
  @Prop({ reflect: true }) overflow?: TableOverflow = TABLE_OVERFLOW.WRAP

  /**
   * Define o estado semântico do tooltip nas células da tabela.
   *
   * @remarks Valores aceitos:
   * - info
   * - warning
   * - danger
   * - success
   *
   * @default info
   */
  @Prop({ reflect: true }) tooltipState?: TableTooltipState = TABLE_TOOLTIP_STATE.INFO

  /**
   * Define o modo de resolução do tooltip nas células da tabela.
   *
   * @remarks Valores aceitos:
   * - inherit
   * - enabled
   * - disabled
   *
   * @default disabled (valor efetivo)
   */
  @Prop({ reflect: true }) tooltipMode?: TableTooltipMode

  /**
   * Ciclo de vida do componente que é chamado após o componente ser carregado no DOM.
   * Inicializa os tokens visuais após a montagem do componente.
   */
  componentDidLoad() {
    this.annotateBoundaries()
    this.registerColumnWidthListener()
    this.registerAlignmentListener()
    this.registerOverflowListener()
    this.applyDensity()
    this.applyDividers()

    // garante cálculo inicial após filhos renderizarem
    queueMicrotask(() => {
      this.updateColumnWidths()
      this.updateAllCellAlignments()
      this.updateAllCellOverflow()
      this.applyAriaMetadata()
    })
  }

  /**
   * Reaplica a largura das colunas quando alterada.
   */
  @Watch('columnWidth')
  protected handleTableColumnWidthChange() {
    this.updateColumnWidths()
  }

  /**
   * Reaplica o token de densidade quando alterado.
   */
  @Watch('density')
  protected handleDensityChange() {
    this.applyDensity()
  }

  /**
   * Atualiza os tokens de divisor quando qualquer propriedade relacionada é alterada.
   */
  @Watch('hasColumnDivider')
  @Watch('hasRowDivider')
  @Watch('dividerStyle')
  protected handleDividerChange() {
    this.applyDividers()
  }

  /**
   * Atualiza os tokens de alinhamento quando alterados.
   */
  @Watch('horizontalAlignment')
  @Watch('verticalAlignment')
  protected handleAlignmentChange() {
    this.updateAllCellAlignments()
  }

  /**
   * Atualiza os tokens de overflow quando a propriedade é alterada.
   */
  @Watch('overflow')
  protected handleOverflowChange() {
    this.updateAllCellOverflow()
  }

  /**
   * Marca a última linha e a última coluna para permitir estilização condicional.
   */
  private annotateBoundaries() {
    const rows = Array.from(this.el.querySelectorAll('br-table-row'))
    const columns = Array.from(this.el.querySelectorAll('br-table-header-cell'))
    const columnCount = columns.length

    columns.forEach((column, columnIndex) => {
      if (!(column instanceof HTMLElement)) return

      const isLastColumn = columnIndex === columnCount - 1
      column.toggleAttribute('data-last-column', isLastColumn)
    })

    rows.forEach((row, rowIndex) => {
      const isLastRow = rowIndex === rows.length - 1
      const cells = Array.from(row.querySelectorAll('br-table-cell'))

      cells.forEach((cell, columnIndex) => {
        if (!(cell instanceof HTMLElement)) return

        const isLastColumn = columnIndex === columnCount - 1
        cell.toggleAttribute('data-last-row', isLastRow)
        cell.toggleAttribute('data-last-column', isLastColumn)
      })
    })
  }

  /**
   * Registra um listener customizado para mudanças na largura das colunas, permitindo que as células reajam dinamicamente a alterações no layout.
   */
  private registerColumnWidthListener() {
    this.el.addEventListener('brColumnWidthChange', () => {
      this.updateColumnWidths()
      this.applyAriaMetadata()
    })
  }

  /**
   * Atualiza a largura das colunas com base nos cabeçalhos.
   * @returns void
   */
  private updateColumnWidths() {
    const headers = this.getHeaderCells()
    if (headers.length === 0) return

    const template = this.buildGridTemplate(headers)
    this.applyGridTemplate(template)
  }

  /**
   * Constrói o template de grid para a tabela com base nos cabeçalhos.
   * @param headers Os elementos de cabeçalho da tabela.
   * @returns Uma string representando o template de grid CSS.
   */
  private buildGridTemplate(headers: HTMLBrTableHeaderCellElement[]): string {
    const tableWidth = this.resolveTableColumnWidth()

    const columnWidths = headers.map((header) => this.resolveColumnWidth(header, tableWidth))

    return columnWidths.join(' ')
  }

  /**
   * Resolve a largura de uma coluna específica com base no cabeçalho e na largura global da tabela.
   */
  private resolveTableColumnWidth(): string {
    return this.resolveWidthToken(this.columnWidth, COLUMN_WIDTH_MAP[COLUMN_WIDTH_SEMANTICS.FILL])
  }

  /**
   * Resolve a largura de uma coluna específica com base no cabeçalho e na largura global da tabela.
   * @param header O elemento de cabeçalho da coluna.
   * @param fallback A largura de fallback a ser usada caso o cabeçalho não defina uma largura.
   * @returns A largura resolvida da coluna.
   */
  private resolveColumnWidth(header: HTMLBrTableHeaderCellElement, fallback: string): string {
    const headerWidth = header.getAttribute('column-width') as ColumnWidth | null
    return this.resolveWidthToken(headerWidth, fallback)
  }

  /**
   * Aplica o template de grid à tabela definindo a propriedade CSS customizada correspondente.
   * @param raw O template de grid bruto a ser aplicado.
   * @param fallback O template de grid de fallback a ser usado caso o valor bruto seja inválido.
   * @return void
   */
  private resolveWidthToken(raw: string | undefined | null, fallback: string): string {
    return resolveTokenValue(raw ?? undefined, {
      fallback,
      semanticMap: COLUMN_WIDTH_MAP,
      validators: [positivePxValidator, positiveFrValidator],
    })
  }

  /**
   * Aplica o template de grid à tabela definindo a propriedade CSS customizada correspondente.
   * @param template O template de grid a ser aplicado.
   */
  private applyGridTemplate(template: string) {
    this.el.style.setProperty(TABLE_TOKENS.COLUMN_WIDTHS, template)
  }

  /**
   * Registra um listener para mudanças de alinhamento, permitindo que as células reajam dinamicamente a alterações nos tokens de alinhamento.
   */
  private registerAlignmentListener() {
    this.el.addEventListener('brAlignmentChange', () => {
      this.updateAllCellAlignments()
      this.applyAriaMetadata()
    })
  }

  /**
   * Atualiza o alinhamento de todas as células da tabela com base nos tokens de alinhamento.
   */
  private updateAllCellAlignments() {
    const cells = this.getAllCells()

    cells.forEach((cell) => {
      this.updateAlignmentForCell(cell)
    })
  }

  /**
   * Atualiza o alinhamento de uma célula específica com base nos tokens de alinhamento.
   * @param cell A célula a ser atualizada.
   */
  private updateAlignmentForCell(cell: HTMLBrTableCellElement) {
    const horizontal = this.resolveHorizontalAlignmentForCell(cell)
    const vertical = this.resolveVerticalAlignmentForCell(cell)

    cell.style.setProperty(TABLE_TOKENS.HORIZONTAL_ALIGNMENT, horizontal)
    cell.style.setProperty(TABLE_TOKENS.VERTICAL_ALIGNMENT, vertical)
  }

  /**
   * Resolve o alinhamento horizontal de uma célula específica com base nos tokens de alinhamento.
   * @param cell A célula a ser avaliada.
   * @returns O valor do token de alinhamento horizontal resolvido.
   */
  private resolveHorizontalAlignmentForCell(cell: HTMLBrTableCellElement): string {
    // Cell
    const cellValue = cell.getAttribute('horizontal-alignment') as TableAlignment | null
    if (cellValue !== null) return this.resolveAlignmentToken(cellValue)

    // Row
    const row = cell.closest('br-table-row')
    const rowValue = row?.getAttribute('horizontal-alignment') as TableAlignment | null
    if (rowValue !== null) return this.resolveAlignmentToken(rowValue)

    // Column (header)
    const columnValue = this.getColumnAlignment(cell, 'horizontal-alignment')
    if (columnValue !== null) return this.resolveAlignmentToken(columnValue)

    // Table
    if (this.horizontalAlignment !== undefined) return this.resolveAlignmentToken(this.horizontalAlignment)

    // Fallback
    return TABLE_ALIGNMENT_MAP[TABLE_ALIGNMENT.START]
  }

  /**
   * Resolve o alinhamento vertical de uma célula específica com base nos tokens de alinhamento.
   * @param cell A célula a ser avaliada.
   * @returns O valor do token de alinhamento vertical resolvido.
   */
  private resolveVerticalAlignmentForCell(cell: HTMLBrTableCellElement): string {
    const cellValue = cell.getAttribute('vertical-alignment') as TableAlignment | null
    if (cellValue !== null) return this.resolveAlignmentToken(cellValue)

    const row = cell.closest('br-table-row')
    const rowValue = row?.getAttribute('vertical-alignment') as TableAlignment | null
    if (rowValue !== null) return this.resolveAlignmentToken(rowValue)

    const columnValue = this.getColumnAlignment(cell, 'vertical-alignment')
    if (columnValue !== null) return this.resolveAlignmentToken(columnValue)

    if (this.verticalAlignment !== undefined) return this.resolveAlignmentToken(this.verticalAlignment)

    return TABLE_ALIGNMENT_MAP[TABLE_ALIGNMENT.START]
  }

  /**
   * Resolve um valor de token de alinhamento com base no valor bruto e no mapeamento semântico definido.
   * @param value O valor bruto do token de alinhamento a ser resolvido.
   * @returns O valor do token de alinhamento resolvido, ou o valor de fallback se o valor bruto for inválido.
   */
  private resolveAlignmentToken(value?: string | null): string {
    return resolveTokenValue(value ?? undefined, {
      fallback: TABLE_ALIGNMENT_MAP[TABLE_ALIGNMENT.START],
      semanticMap: TABLE_ALIGNMENT_MAP,
    })
  }

  /**
   * Retorna o valor do atributo de alinhamento de uma coluna específica.
   * @param cell A célula para a qual o alinhamento da coluna deve ser obtido.
   * @param attribute O atributo de alinhamento a ser obtido ('horizontal-alignment' ou 'vertical-alignment').
   * @returns O valor do atributo de alinhamento da coluna, ou null se não estiver definido.
   */
  private getColumnAlignment(
    cell: HTMLBrTableCellElement,
    attribute: 'horizontal-alignment' | 'vertical-alignment'
  ): string | null {
    const columnIndex = this.getColumnIndex(cell)
    if (columnIndex === -1) return null

    const headers = this.getHeaderCells()
    const header = headers[columnIndex]
    return header?.getAttribute(attribute) ?? null
  }

  /**
   * Obtém o índice da coluna para uma célula específica, permitindo a associação entre células e seus respectivos cabeçalhos para herança de propriedades como alinhamento e largura.
   * @param cell A célula para a qual o índice da coluna deve ser obtido.
   * @returns O índice da coluna correspondente à célula, ou -1 se a célula não estiver dentro de uma linha válida.
   */
  private getColumnIndex(cell: HTMLBrTableCellElement): number {
    const row = cell.closest('br-table-row') as HTMLBrTableRowElement | null
    if (row === null) return -1

    const cells = Array.from(row.querySelectorAll('br-table-cell'))
    return cells.indexOf(cell)
  }

  /**
   * Constrói o template de grid para a tabela com base nos cabeçalhos.
   * @return Array de larguras de coluna resolvidas.
   */
  private getHeaderCells(): HTMLBrTableHeaderCellElement[] {
    return Array.from(this.el.querySelectorAll('br-table-header-cell')) as HTMLBrTableHeaderCellElement[]
  }

  /**
   * Obtém todas as células (tanto de corpo quanto de cabeçalho) da tabela, permitindo a aplicação de estilos e tokens de forma abrangente.
   * @returns Um array contendo todas as células da tabela.
   */
  private getAllCells(): HTMLBrTableCellElement[] {
    return Array.from(this.el.querySelectorAll('br-table-cell, br-table-header-cell')) as HTMLBrTableCellElement[]
  }

  /**
   * Registra um listener para mudanças no overflow, permitindo que as células reajam dinamicamente a alterações no token de overflow.
   */
  private registerOverflowListener() {
    this.el.addEventListener('brOverflowChange', () => {
      this.updateAllCellOverflow()
      this.applyAriaMetadata()
    })
  }

  /**
   * Recalcula estrutura, tokens e metadados de acessibilidade quando há mudança de slots.
   */
  private handleStructureChange = () => {
    this.annotateBoundaries()
    this.updateColumnWidths()
    this.updateAllCellAlignments()
    this.updateAllCellOverflow()
    this.applyAriaMetadata()
  }

  /**
   * Aplica metadados ARIA para melhorar a navegação por leitores de tela.
   */
  private applyAriaMetadata() {
    const rows = Array.from(this.el.querySelectorAll('br-table-row'))
    const headers = this.getHeaderCells()

    this.el.setAttribute('aria-colcount', String(headers.length))
    this.el.setAttribute('aria-rowcount', String(rows.length))

    rows.forEach((row, rowIndex) => {
      row.setAttribute('aria-rowindex', String(rowIndex + 1))
    })

    headers.forEach((header, headerIndex) => {
      const colIndex = String(headerIndex + 1)
      const headerId = header.id || `${this.generatedHeaderPrefix}-${headerIndex + 1}`

      header.id = headerId
      header.setAttribute('aria-colindex', colIndex)
    })

    const bodyCells = Array.from(this.el.querySelectorAll('br-table-cell'))
    bodyCells.forEach((cell) => {
      const columnIndex = this.getColumnIndex(cell)
      if (columnIndex === -1) return

      const colIndex = String(columnIndex + 1)
      const header = headers[columnIndex]

      cell.setAttribute('aria-colindex', colIndex)

      if (header !== undefined && header.id !== '') {
        cell.setAttribute('aria-labelledby', header.id)
      }
    })
  }

  /**
   * Atualiza os tokens de overflow para todas as células da tabela, garantindo que as mudanças na propriedade de overflow sejam refletidas dinamicamente em todas as células, controlando o comportamento de overflow, text-overflow e white-space conforme definido no token.
   */
  private updateAllCellOverflow() {
    this.getAllCells().forEach((cell) => {
      this.updateOverflowForCell(cell)
    })
  }

  /**
   * Atualiza os tokens de overflow para uma célula específica, controlando o comportamento de overflow, text-overflow e white-space com base na resolução do token de overflow para a célula, que considera atributos específicos da célula, da linha, da coluna e da tabela para determinar o comportamento final de overflow a ser aplicado.
   * @param cell A célula para a qual os tokens de overflow devem ser atualizados.
   */
  private updateOverflowForCell(cell: HTMLElement) {
    const mode = this.resolveOverflowForCell(cell)
    const config = TABLE_OVERFLOW_MAP[mode]

    cell.style.setProperty(TABLE_TOKENS.CELL_OVERFLOW, config.overflow)
    cell.style.setProperty(TABLE_TOKENS.CELL_TEXT_OVERFLOW, config.textOverflow)
    cell.style.setProperty(TABLE_TOKENS.CELL_WHITE_SPACE, config.whiteSpace)

    // O atributo 'applied-overflow' deve sempre aparecer
    cell.setAttribute('data-applied-overflow', mode)
  }

  /**
   * Resolve o token de overflow para uma célula específica, considerando a hierarquia de atributos (célula, linha, coluna, tabela) para determinar o comportamento de overflow final a ser aplicado à célula.
   * @param cell A célula para a qual o token de overflow deve ser resolvido.
   * @returns O valor do token de overflow resolvido para a célula, determinando o comportamento de overflow a ser aplicado.
   */
  private resolveOverflowForCell(cell: HTMLElement): TableOverflow {
    // Resolução hierárquica: célula > linha > coluna > tabela > fallback
    let value = cell.getAttribute('overflow')
    if (value === null) {
      const row = cell.closest('br-table-row')
      value = row?.getAttribute('overflow') ?? null
    }
    if (value === null) {
      value = this.getColumnAttribute(cell, 'overflow') ?? null
    }
    if (value === null) {
      value = this.overflow ?? null
    }
    if (value === null) {
      value = TABLE_OVERFLOW.WRAP
    }

    return value === TABLE_OVERFLOW.TRUNCATE ? TABLE_OVERFLOW.TRUNCATE : TABLE_OVERFLOW.WRAP
  }

  /**
   * Obtém o valor de um atributo específico de uma coluna para uma célula, permitindo a herança de propriedades da coluna para as células associadas com base no cabeçalho correspondente.
   * @param cell A célula para a qual o atributo da coluna deve ser obtido.
   * @param attribute O atributo específico a ser obtido da coluna (por exemplo, 'horizontal-alignment' ou 'vertical-alignment').
   * @returns O valor do atributo da coluna correspondente à célula, ou null se não estiver definido.
   */
  private getColumnAttribute(cell: HTMLElement, attribute: string): string | null {
    const header = this.getHeaderForCell(cell)
    return header?.getAttribute(attribute) ?? null
  }

  /**
   * Obtém o elemento de cabeçalho correspondente a uma célula específica, permitindo a associação entre células e seus respectivos cabeçalhos para herança de propriedades como alinhamento e largura.
   * @param cell A célula para a qual o cabeçalho correspondente deve ser obtido.
   * @returns O elemento de cabeçalho correspondente à célula, ou null se não for encontrado.
   */
  private getHeaderForCell(cell: HTMLElement): HTMLElement | null {
    const index = this.getColumnIndex(cell as HTMLBrTableCellElement)
    if (index === -1) return null

    const headers = this.getHeaderCells()
    return headers[index] ?? null
  }

  /**
   * Resolve e aplica o token de densidade.
   */
  private applyDensity() {
    const resolved = resolveTokenValue(this.density, {
      fallback: TABLE_DENSITY_MAP[TABLE_DENSITY.MEDIUM],
      semanticMap: TABLE_DENSITY_MAP,
    })

    this.el.style.setProperty(TABLE_TOKENS.DENSITY, resolved)
  }

  /**
   * Resolve e aplica os tokens relacionados aos divisores
   * (estilo e fatores de linha/coluna).
   */
  private applyDividers() {
    const style = resolveTokenValue(this.dividerStyle, {
      fallback: TABLE_DIVIDER_STYLE_MAP[TABLE_DIVIDER_STYLE.SOLID],
      semanticMap: TABLE_DIVIDER_STYLE_MAP,
    })

    const rowFactor = this.hasRowDivider ? '1' : '0'
    const columnFactor = this.hasColumnDivider ? '1' : '0'

    this.el.style.setProperty(TABLE_TOKENS.DIVIDER_STYLE, style)
    this.el.style.setProperty(TABLE_TOKENS.ROW_DIVIDER_FACTOR, rowFactor)
    this.el.style.setProperty(TABLE_TOKENS.COLUMN_DIVIDER_FACTOR, columnFactor)
  }

  /**
   * Retorna o mapa de classes base do componente.
   *
   * @returns Um objeto representando as classes CSS a serem aplicadas ao elemento da tabela.
   */
  private getTableCssClassMap(): CssClassMap {
    return {
      'br-table': true,
    }
  }

  /**
   * Renderiza a estrutura interna da tabela (header e body).
   *
   * @returns JSX.Element representando o conteúdo da tabela, incluindo os slots para header e body.
   */
  private renderContent() {
    return (
      <div class="br-table-content">
        <slot name="header" onSlotchange={this.handleStructureChange}></slot>
        <slot name="body" onSlotchange={this.handleStructureChange}></slot>
      </div>
    )
  }

  /**
   * Renderiza o host do componente.
   *
   * @returns JSX.Element representando o elemento host do componente, contendo a estrutura da tabela.
   */
  render() {
    return (
      <Host role="table">
        <div class={this.getTableCssClassMap()}>{this.renderContent()}</div>
      </Host>
    )
  }
}
